/**
 * Address input with Google Places autocomplete — the React port of
 * site/portal/assets/address.js, same contracts:
 *
 * - Same referrer-restricted browser key, served by /api/auth/config.
 * - Session token groups keystrokes with the detail fetch for billing.
 * - Degrades to a plain input if the key is absent, the API is off, or Google
 *   is unreachable; a 403 disables it for the page rather than per keystroke.
 */
import { useEffect, useRef, useState } from 'react';
import { loadConfig } from '../api';

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const DETAILS_URL = 'https://places.googleapis.com/v1/places/';

export interface PlaceFill {
  line1: string;
  city: string;
  state: string;
  zip: string;
  county: string;
}

interface Suggestion {
  placeId: string;
  text: string;
}

let apiKey: string | null = null;
let enabled = false;
void loadConfig()
  .then((cfg) => {
    apiKey = cfg.apiKey;
    enabled = !!apiKey;
  })
  .catch(() => undefined);

function component(place: { addressComponents?: Array<{ types?: string[]; longText?: string; shortText?: string }> }, type: string, short = false): string {
  const found = (place.addressComponents ?? []).find((c) => (c.types ?? []).includes(type));
  return (short ? found?.shortText : found?.longText) ?? '';
}

export function AddressInput({
  id,
  value,
  onChange,
  onPlace,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onPlace: (place: PlaceFill) => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [active, setActive] = useState(-1);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const session = useRef<string | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  function close() {
    setSuggestions([]);
    setActive(-1);
  }

  function query(input: string) {
    if (!session.current) session.current = crypto.randomUUID();
    fetch(AUTOCOMPLETE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': apiKey! },
      body: JSON.stringify({ input, sessionToken: session.current, includedRegionCodes: ['us'] }),
    })
      .then((res) => {
        if (res.status === 403) {
          enabled = false;
          close();
          return null;
        }
        return res.ok ? res.json() : null;
      })
      .then((data: { suggestions?: Array<{ placePrediction?: { placeId: string; text?: { text?: string } } }> } | null) => {
        if (!data) return;
        setSuggestions(
          (data.suggestions ?? [])
            .map((s) => s.placePrediction)
            .filter((p): p is NonNullable<typeof p> => !!p)
            .slice(0, 5)
            .map((p) => ({ placeId: p.placeId, text: p.text?.text ?? '' })),
        );
        setActive(-1);
      })
      .catch(close);
  }

  function choose(index: number) {
    const s = suggestions[index];
    if (!s) return;
    const token = session.current;
    session.current = null;
    close();

    fetch(`${DETAILS_URL}${encodeURIComponent(s.placeId)}?sessionToken=${encodeURIComponent(token ?? '')}`, {
      headers: { 'X-Goog-Api-Key': apiKey!, 'X-Goog-FieldMask': 'addressComponents' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((place) => {
        if (!place) return;
        const streetNumber = component(place, 'street_number');
        const route = component(place, 'route');
        const subpremise = component(place, 'subpremise');
        let line1 = [streetNumber, route].filter(Boolean).join(' ');
        if (subpremise) line1 += `, ${subpremise}`;
        onPlace({
          line1,
          city: component(place, 'locality') || component(place, 'sublocality') || component(place, 'postal_town'),
          state: component(place, 'administrative_area_level_1', true),
          zip: component(place, 'postal_code'),
          county: component(place, 'administrative_area_level_2').replace(/\s+County$/i, ''),
        });
      })
      .catch(() => undefined); // the typed text stands
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={value}
        style={{ width: '100%' }}
        onChange={(e) => {
          onChange(e.target.value);
          if (!enabled) return;
          clearTimeout(timer.current);
          const next = e.target.value.trim();
          if (next.length < 4) return close();
          timer.current = setTimeout(() => query(next), 120);
        }}
        onKeyDown={(e) => {
          if (!suggestions.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, suggestions.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault();
            choose(active);
          } else if (e.key === 'Escape') {
            close();
          }
        }}
        onBlur={() => setTimeout(close, 150)}
      />
      {suggestions.length > 0 && (
        <div className="suggest">
          {suggestions.map((s, i) => (
            <button
              key={s.placeId}
              type="button"
              className={'suggest__item' + (i === active ? ' is-active' : '')}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
            >
              {s.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
